import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

const linkFeatures = [
  'Destination URL validation for http/https links',
  'Random short codes or custom aliases',
  'Title, description, tags, and notes',
  'Campaign assignment for grouped reporting',
  'UTM parameters appended to the destination at redirect time',
  'Optional password gate',
  'Optional expiration date and time',
  'Optional maximum successful-click limit',
  'Configurable redirect status code',
  'Soft delete and restore through admin workflows',
];

const analyticsFields = [
  'Successful, blocked, expired, password-gated, and max-click-blocked events',
  'Qualified-click, bot/scanner, and suspicious-click counts',
  'Bot classification and reason breakdowns for scanners, preview crawlers, known bots, and likely bots',
  'Country, state / region, city, postal code, continent, timezone, and approximate coordinates',
  'Redacted IP display in the portal and CSV exports',
  'Network / ASN organization and edge location',
  'Device type, browser, operating system, user agent, and referrer',
  'Timeline charts, unique-visitor estimates, and top breakdowns',
  'Per-link, campaign-level, organization-level, and admin-level analytics views',
];

const roleRows = [
  ['viewer', 'Can view links and analytics where permitted.'],
  ['member', 'Can create and manage links according to workspace policy.'],
  ['org_admin', 'Can manage organization links, campaigns, and users.'],
  ['org_owner', 'Organization owner with elevated workspace permissions.'],
  ['admin', 'Platform administrator with access to the admin console.'],
  ['super_admin', 'Highest platform role for full administrative access.'],
] as const;

export function DocumentationPage() {
  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 text-ink-900 dark:text-ink-50">
      <header className="border-b border-ink-200 dark:border-ink-800 bg-white/80 dark:bg-ink-900/80 backdrop-blur">
        <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 font-bold tracking-tight">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 text-white">
              S
            </span>
            SLS Short
          </Link>
          <nav className="flex items-center gap-3 text-sm">
            <Link to="/login" className="btn-ghost">
              Sign in
            </Link>
            <Link to="/login?signup=1" className="btn-primary">
              Get started
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12 lg:py-16">
        <section className="grid gap-8 lg:grid-cols-[1fr_280px] items-start">
          <div>
            <p className="text-sm font-medium text-brand-600 dark:text-brand-300">
              Product documentation
            </p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
              Build, measure, and manage short links with confidence.
            </h1>
            <p className="mt-5 text-lg text-ink-600 dark:text-ink-300 max-w-3xl">
              This guide covers the current end-user features in SLS Short, including link
              creation, UTM parameters, QR codes, campaigns, analytics, CSV exports, and
              administrator workflows.
            </p>
          </div>
          <aside className="card p-4 sticky top-6 hidden lg:block">
            <h2 className="text-sm font-semibold mb-3">On this page</h2>
            <ul className="space-y-2 text-sm text-ink-600 dark:text-ink-300">
              <li><a className="hover:text-brand-600" href="#quick-start">Quick start</a></li>
              <li><a className="hover:text-brand-600" href="#links">Short links</a></li>
              <li><a className="hover:text-brand-600" href="#utm">UTM parameters</a></li>
              <li><a className="hover:text-brand-600" href="#qr">QR codes</a></li>
              <li><a className="hover:text-brand-600" href="#campaigns">Campaigns</a></li>
              <li><a className="hover:text-brand-600" href="#analytics">Analytics</a></li>
              <li><a className="hover:text-brand-600" href="#security">Security controls</a></li>
              <li><a className="hover:text-brand-600" href="#admin">Admin console</a></li>
              <li><a className="hover:text-brand-600" href="#exports">Import and export</a></li>
            </ul>
          </aside>
        </section>

        <div className="mt-12 space-y-8">
          <DocSection id="quick-start" title="Quick start">
            <ol className="list-decimal pl-5 space-y-2 text-ink-600 dark:text-ink-300">
              <li>Sign in to your workspace.</li>
              <li>Open <strong>Links</strong>, then choose <strong>New short link</strong>.</li>
              <li>Enter the destination URL and optional metadata such as title, tags, campaign, and UTM values.</li>
              <li>Add protection controls if needed: password, expiration date, max clicks, or redirect status.</li>
              <li>Create the link, copy the short URL, and use the link detail page to view analytics and QR codes.</li>
            </ol>
          </DocSection>

          <DocSection id="links" title="Short links">
            <p>
              Short links point to a destination URL and add management, analytics, and protection
              features around the redirect. Use random generated codes for speed, or set a custom
              alias when the short URL needs to be memorable.
            </p>
            <FeatureList items={linkFeatures} />
          </DocSection>

          <DocSection id="utm" title="UTM parameters when creating a link">
            <p>
              UTM parameters are tracking tags appended to the destination URL so analytics tools
              can identify where traffic came from. They are useful when the same destination is
              shared across multiple channels, such as email, paid ads, social posts, QR codes, or
              partner campaigns.
            </p>
            <div className="overflow-auto mt-4">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Purpose</th>
                    <th>Examples</th>
                  </tr>
                </thead>
                <tbody>
                  <UtmRow field="utm_source" purpose="Where the visitor came from" examples="newsletter, linkedin, google, partner-acme" />
                  <UtmRow field="utm_medium" purpose="The marketing channel type" examples="email, social, cpc, qr, referral" />
                  <UtmRow field="utm_campaign" purpose="The campaign or promotion name" examples="spring-launch, black-friday, webinar-q2" />
                  <UtmRow field="utm_term" purpose="Paid-search keyword or audience term" examples="url+shortener, enterprise+links" />
                  <UtmRow field="utm_content" purpose="Variant, placement, or A/B test label" examples="hero-button, footer-link, ad-a" />
                </tbody>
              </table>
            </div>
            <div className="mt-4 rounded-lg bg-ink-900 text-white p-4 text-xs font-mono overflow-auto">
              https://example.com/pricing?utm_source=newsletter&utm_medium=email&utm_campaign=spring-launch&utm_content=hero-button
            </div>
            <p className="mt-4">
              Recommended convention: use lowercase values, avoid spaces, and be consistent. For
              example, pick either <code className="font-mono">linkedin</code> or{' '}
              <code className="font-mono">linked_in</code> and reuse it everywhere. UTM source,
              medium, and campaign are the most important fields; term and content are optional.
            </p>
          </DocSection>

          <DocSection id="qr" title="QR codes">
            <p>
              Every short link includes a QR code that points to the short URL, not the destination
              URL. This keeps analytics, password protection, expiration, and click limits in the
              redirect path even when the visitor scans from print or signage.
            </p>
            <FeatureList
              items={[
                'Download QR codes as SVG, PNG, or PDF from the link detail page',
                'Customize foreground and background colors',
                'Choose error-correction level and margin',
                'Choose print-safe size presets for labels, handouts, and posters',
                'QR downloads point to the short URL with QR-specific UTM values so scans can be separated in analytics',
              ]}
            />
          </DocSection>

          <DocSection id="campaigns" title="Campaigns">
            <p>
              Campaigns group related short links so you can report on a launch, email send,
              event, promotion, or partner program as a single unit. A campaign can have many
              links, and each link can still keep its own analytics and settings.
            </p>
            <FeatureList
              items={[
                'Create campaigns with name, description, start date, and end date',
                'Assign links to a campaign during link creation or editing',
                'View campaign-level click activity and linked URLs',
                'Use campaign-level reporting alongside UTM campaign values when needed',
              ]}
            />
          </DocSection>

          <DocSection id="analytics" title="Analytics">
            <p>
              Analytics are collected for click attempts and successful redirects. The portal shows
              per-link analytics, campaign analytics, workspace summaries, and platform-wide admin
              analytics for administrators.
            </p>
            <FeatureList items={analyticsFields} />
            <p className="mt-4">
              IP addresses are stored for abuse investigation, but the web portal and CSV exports
              display redacted networks only. IPv4 addresses show as a /24 network, and IPv6
              addresses show as a /48 network.
            </p>
          </DocSection>

          <DocSection id="security" title="Security controls">
            <FeatureList
              items={[
                'Password-protected links with short-lived access cookies after successful entry',
                'Expiration dates that block redirects after the configured time',
                'Maximum click limits that stop redirects after the allowed successful-click count',
                'Disabled-link state for pausing access without deleting the link',
                'Reserved slug protection for system paths such as app, admin, and API routes',
                'Role-based access control across viewer, member, organization, and platform-admin roles',
                'Audit logs for sensitive admin and authentication activity',
              ]}
            />
          </DocSection>

          <DocSection id="admin" title="Admin console">
            <p>
              Users with the <code className="font-mono">admin</code> or{' '}
              <code className="font-mono">super_admin</code> role can access the admin console.
              Non-admin users do not see the Admin Console entry in the app navigation.
            </p>
            <div className="overflow-auto mt-4">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Typical access</th>
                  </tr>
                </thead>
                <tbody>
                  {roleRows.map(([role, access]) => (
                    <tr key={role}>
                      <td className="font-mono text-xs">{role}</td>
                      <td>{access}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <FeatureList
              items={[
                'Platform dashboard with total organizations, users, links, clicks, and recent audit activity',
                'User management for creating users, assigning organizations, roles, and status',
                'Organization management for creating, editing, and disabling workspaces',
                'Global link, campaign, and analytics visibility across the platform',
                'Audit log viewer for sensitive actions',
                'Back to app navigation for returning to the customer dashboard',
              ]}
            />
          </DocSection>

          <DocSection id="exports" title="Import and export">
            <FeatureList
              items={[
                'Export workspace links to CSV from the links page',
                'Export per-link events to CSV from the link detail page',
                'Event CSV exports include redacted IP, location, network, timezone, device, browser, OS, and referrer data',
                'Bulk link creation by CSV is supported through the API and intended for larger imports',
              ]}
            />
          </DocSection>
        </div>
      </main>
    </div>
  );
}

function DocSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="card p-6 scroll-mt-6">
      <h2 className="text-2xl font-bold tracking-tight mb-4">{title}</h2>
      <div className="space-y-3 text-ink-600 dark:text-ink-300 leading-7">{children}</div>
    </section>
  );
}

function FeatureList({ items }: { items: string[] }) {
  return (
    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2 text-sm">
          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-500 flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function UtmRow({ field, purpose, examples }: { field: string; purpose: string; examples: string }) {
  return (
    <tr>
      <td className="font-mono text-xs">{field}</td>
      <td>{purpose}</td>
      <td>{examples}</td>
    </tr>
  );
}
